import { useState, useEffect } from 'react';

/**
 * Hook to generate thumbnails from video URLs
 * @param videoUrl URL of the video to generate thumbnail from
 * @param timePosition Position in seconds to capture the thumbnail (defaults to 1s)
 * @returns Object containing the generated thumbnail URL and loading state
 */
export function useVideoThumbnail(videoUrl: string | null, timePosition = 1) {
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!videoUrl) {
      setThumbnail(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const generateThumbnail = () => {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.src = videoUrl;
      video.muted = true;
      video.preload = 'metadata';

      // Generate thumbnail after metadata is loaded
      video.onloadedmetadata = () => {
        // Seek to the specified position in the video
        video.currentTime = timePosition;
      };

      // After seeking, capture the frame
      video.onseeked = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const thumbnailUrl = canvas.toDataURL('image/jpeg');
            setThumbnail(thumbnailUrl);
            setIsLoading(false);
          } else {
            throw new Error('Failed to get canvas context');
          }
        } catch (err) {
          setError('Error generating video thumbnail');
          setIsLoading(false);
          console.error('Error generating video thumbnail:', err);
        }
      };

      // Handle errors
      video.onerror = () => {
        setError('Error loading video');
        setIsLoading(false);
        console.error('Error loading video for thumbnail generation');
      };
    };

    generateThumbnail();
  }, [videoUrl, timePosition]);

  return { thumbnail, isLoading, error };
}
